import React from "react";

import { isEmpty, parseReference } from "@medplum/core";

import { Stack, Table, Text } from "@mantine/core";

import { format, parseISO } from "date-fns";
import dompurify from "dompurify";
import omit from "lodash/omit";
import trim from "lodash/trim";

import { TextWrapper } from "@/components/TextWrapper";
import { PrenatalFlowsheetFormType } from "@/encounters/zod-schemas/encounter-answers";
import { LOINCCodes } from "@/fhir/enums/loinc";
import { findObservationByCode } from "@/fhir/observation";
import { calculateGestationalAgeInWeeksAndDays } from "@/pregnancy";
import { localDateStr, parseDateTimeFormat } from "@/utils/datetime";

import type { PrenatalFlowsheetObservationRow } from "./hooks";
import { ObservationValueDisplay } from "./ObservationValueDisplay";
import { WeightWithChangeDisplay } from "./WeightWithChangeDisplay";

export const PrenatalFlowsheetFormRows = ({
  formValues,
  gestationalAge,
}: {
  formValues: PrenatalFlowsheetFormType;
  gestationalAge: { weeks: number; days: number } | undefined;
}) => {
  if (isEmpty(omit(formValues, ["date"]))) return;

  const parsedDate = parseDateTimeFormat(formValues.encounterFlowSheetDate?.toString());
  const formDate = localDateStr(parsedDate ? parsedDate.toISOString() : "");

  const weightDisplay = trim(
    `${formValues.encounterFlowSheetWeight ?? ""} ${formValues.encounterFlowSheetWeightUnit ?? ""}`,
  );
  const fundalHeightDisplay = trim(
    `${formValues.encounterFlowSheetFundalHeight ?? ""} ${formValues.encounterFlowSheetFundalHeightUnit ?? ""}`,
  );

  const hasAnyValues = Object.values(formValues).some((value) => value);

  const isNegativeGestation = gestationalAge && gestationalAge.weeks && gestationalAge.weeks < 0;

  if (!hasAnyValues) return;

  return (
    <>
      <Table.Tr role="row">
        <Table.Td role="cell">
          {gestationalAge && !isNegativeGestation && (
            <Stack gap={0}>
              <span>{isNaN(gestationalAge.weeks) ? "" : gestationalAge.weeks}</span>
              <span>{isNaN(gestationalAge.days) ? "" : gestationalAge.days}</span>
            </Stack>
          )}
        </Table.Td>
        <Table.Td role="cell">{formDate ? format(parseISO(formDate), "MM/dd/yy") : ""}</Table.Td>
        <Table.Td role="cell">
          <TextWrapper>{weightDisplay}</TextWrapper>
        </Table.Td>
        <Table.Td role="cell">
          <TextWrapper>
            {formValues.encounterFlowSheetSystolicBP}
            {formValues.encounterFlowSheetSystolicBP &&
              formValues.encounterFlowSheetDiastolicBP &&
              "/"}
            {formValues.encounterFlowSheetDiastolicBP}
          </TextWrapper>
        </Table.Td>
        <Table.Td role="cell">
          <TextWrapper>{formValues.encounterFlowSheetHeartRate}</TextWrapper>
        </Table.Td>
        <Table.Td role="cell">
          <TextWrapper>{fundalHeightDisplay}</TextWrapper>
        </Table.Td>
        <Table.Td role="cell">
          <TextWrapper>{formValues.encounterFlowSheetFetalHeartRate}</TextWrapper>
        </Table.Td>
        <Table.Td role="cell">
          <TextWrapper>{formValues.encounterFlowSheetFetalPresentation}</TextWrapper>
        </Table.Td>
        <Table.Td role="cell">
          <TextWrapper>{formValues.encounterFlowSheetFetalMovement?.display}</TextWrapper>
        </Table.Td>
        <Table.Td role="cell">
          <TextWrapper>{formValues.encounterFlowSheetEdema?.display}</TextWrapper>
        </Table.Td>
        <Table.Td role="cell">
          <TextWrapper>
            {formValues.encounterFlowSheetUrineProtein?.display}
            {formValues.encounterFlowSheetUrineProtein &&
              formValues.encounterFlowSheetUrineGlucose && <br />}
            {formValues.encounterFlowSheetUrineGlucose?.display}
          </TextWrapper>
        </Table.Td>
      </Table.Tr>
      {formValues.encounterFlowSheetNotes && (
        <Table.Tr role="row">
          <Table.Td role="cell" colSpan={12}>
            <Text dangerouslySetInnerHTML={{ __html: formValues.encounterFlowSheetNotes }} />
          </Table.Td>
        </Table.Tr>
      )}
    </>
  );
};

export const PrenatalFlowsheetObservationsRows = ({
  eddDate,
  observations,
  weightChanges,
}: {
  eddDate: string | undefined;
  observations: PrenatalFlowsheetObservationRow[];
  weightChanges: Map<string, number>;
}) => {
  if (isEmpty(observations)) return;

  return observations.map(([flowsheetObservation, members, encounterPeriodStart]) => {
    const date = flowsheetObservation.effectiveDateTime ?? flowsheetObservation.meta!.lastUpdated!;
    const gaResult =
      eddDate && encounterPeriodStart
        ? calculateGestationalAgeInWeeksAndDays(eddDate, encounterPeriodStart)
        : undefined;
    const gestationalAge = gaResult && gaResult.weeks >= 0 ? gaResult : undefined;

    const bloodPressureObservation = findObservationByCode(members, LOINCCodes.BloodPressure);
    const urineProteinObservation = findObservationByCode(
      members,
      LOINCCodes.UrineProteinTestStrip,
    );
    const urineGlucoseObservation = findObservationByCode(members, LOINCCodes.UrineGlucose);
    const heartRateObservation = findObservationByCode(members, LOINCCodes.HeartRate);
    const fundalHeightObservation = findObservationByCode(members, LOINCCodes.FundalHeight);
    const fetalHeartRateObservation = findObservationByCode(members, LOINCCodes.FetalHeartRate);
    const fetalPositionObservation = findObservationByCode(members, LOINCCodes.FetalPosition);
    const fetalMovementObservation = findObservationByCode(members, LOINCCodes.FetalMovement);
    const weightMeasuredObservation = findObservationByCode(members, LOINCCodes.WeightMeasured);
    const edemaObservation = findObservationByCode(members, LOINCCodes.Edema);
    const notes = flowsheetObservation?.note?.[0]?.text;
    const questionnaireResponseReference = flowsheetObservation.derivedFrom?.find(
      (value) => parseReference(value)[0] === "QuestionnaireResponse",
    )?.reference;

    return (
      <React.Fragment key={`${date}${questionnaireResponseReference}`}>
        <Table.Tr role="row">
          <Table.Td role="cell">
            {/* Gestational Age */}
            {gestationalAge ? (
              <Stack gap={0}>
                <span>{isNaN(gestationalAge.weeks) ? "" : gestationalAge.weeks}</span>
                <span>{isNaN(gestationalAge.days) ? "" : gestationalAge.days}</span>
              </Stack>
            ) : (
              ""
            )}
          </Table.Td>
          <Table.Td role="cell">
            {/* Date */}
            {date ? format(parseISO(date), "MM/dd/yy") : ""}
          </Table.Td>
          <Table.Td role="cell">
            {/* Weight */}
            <WeightWithChangeDisplay
              change={weightChanges.get(flowsheetObservation.id)}
              weightDisplay={<ObservationValueDisplay observation={weightMeasuredObservation} />}
            />
          </Table.Td>
          <Table.Td role="cell">
            <ObservationValueDisplay
              span
              showUnit={false}
              observation={bloodPressureObservation?.component?.[0]} // Systolic
            />
            {bloodPressureObservation && "/"}
            <ObservationValueDisplay
              span
              showUnit={false}
              observation={bloodPressureObservation?.component?.[1]} // Diastolic
            />
          </Table.Td>
          <Table.Td role="cell">
            <ObservationValueDisplay showUnit={false} observation={heartRateObservation} />
          </Table.Td>
          <Table.Td role="cell">
            <ObservationValueDisplay observation={fundalHeightObservation} />
          </Table.Td>
          <Table.Td role="cell">
            {/* FHT = Fetal Heart Rate */}
            <ObservationValueDisplay observation={fetalHeartRateObservation} />
          </Table.Td>
          <Table.Td role="cell">
            <ObservationValueDisplay observation={fetalPositionObservation} />
          </Table.Td>
          <Table.Td role="cell">
            {/* FM = Fetal Movement */}
            <ObservationValueDisplay observation={fetalMovementObservation} />
          </Table.Td>
          <Table.Td role="cell">
            <ObservationValueDisplay observation={edemaObservation} />
          </Table.Td>
          <Table.Td role="cell">
            <ObservationValueDisplay span observation={urineProteinObservation} />
            {urineProteinObservation && urineGlucoseObservation && <br />}
            <ObservationValueDisplay span observation={urineGlucoseObservation} />
          </Table.Td>
        </Table.Tr>

        {notes && (
          <Table.Tr role="row">
            <Table.Td role="cell" colSpan={12}>
              <Text fz="md" dangerouslySetInnerHTML={{ __html: dompurify.sanitize(notes) }} />
            </Table.Td>
          </Table.Tr>
        )}
      </React.Fragment>
    );
  });
};
